import { getSaldosIniciales } from "../saldosIniciales";
import { client } from "../supabase";
import { toDisplayCurrency } from "./currency";
import { construirLedgerProveedor } from "./ledgerProveedor";
import { encontrarSaldoInicial, FECHA_CORTE } from "./saldoInicial";

// Saldo FIFO pendiente por factura de un proveedor. FUENTE ÚNICA: reusa construirLedgerProveedor
// (el motor canónico del ledger de EstadoCuenta) — NO reimplementa el FIFO (regla 10.1). Solo
// orquesta el fetch de los mismos inputs que usa EstadoCuenta + la llamada al motor.
//
// Devuelve el `saldosPorFactura` del ledger: Map keyed "EXT:{id}" / "SII:{id}" → { saldoDisplay,
// factura }, que incluye SOLO las facturas con saldo > 0.01 (las totalmente amortizadas quedan
// fuera). `saldoDisplay` es el REMANENTE (no el total) tras aplicar pagos a cuenta (FIFO) + MBF +
// NCs (10.42/10.43). El saldo queda expresado en la moneda del proveedor (moneda_default) — misma
// convención que FlujoCajaV2 y el ledger de EstadoCuenta para un proveedor USD.
//
// Self-contained a propósito: el consumidor (selector de "Vincular existente") obtiene el saldo real
// lo abra quien lo abra (FlujoCajaV2, EstadoCuenta, MovimientosBancarios), sin depender del caller.
export const getSaldosPorFacturaProveedor = async (provId, pais) => {
  const monedaPrincipal = pais === "Perú" ? "PEN" : "CLP";

  const [provRes, cajaRes, extRes, siiRes, mbfRes, saldosIniciales] = await Promise.all([
    client.from("proveedores").select("id, moneda_default").eq("id", provId).maybeSingle(),
    client
      .from("movimientos_caja")
      .select(
        "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado, movimiento_bancario_id"
      )
      .eq("pais", pais)
      .neq("estado", "anulado")
      .gte("fecha", FECHA_CORTE),
    client
      .from("facturas_externas")
      .select(
        "id, proveedor_id, fecha_emision, monto_total, moneda, numero_documento, estado, concepto, archivo_url, periodo_mes, movimiento_bancario_id"
      )
      .eq("pais", pais),
    client
      .from("facturas_sii_recibidas")
      .select("id, proveedor_id, fecha_emision, monto_total, folio, tipo_dte, razon_social_emisor, estado_vinculacion")
      .eq("pais", pais)
      .gte("fecha_emision", FECHA_CORTE),
    client.from("movimiento_bancario_facturas").select("movimiento_bancario_id, factura_tipo, factura_id, monto"),
    getSaldosIniciales(pais),
  ]);

  for (const r of [provRes, cajaRes, extRes, siiRes, mbfRes]) {
    if (r.error) throw r.error;
  }

  const provMoneda = provRes.data?.moneda_default || monedaPrincipal;
  const saldoInicial = encontrarSaldoInicial(saldosIniciales || [], "proveedor", provId);
  // Conversión a la moneda del proveedor, con el util canónico (10.1) — mismo criterio que
  // EstadoCuenta (monedaSaldo) y FlujoCajaV2 (tdcProv) para un proveedor USD.
  const toDisp = (monto, monedaOrigen, fecha) =>
    toDisplayCurrency(monto, monedaOrigen, fecha, { monedaSaldo: provMoneda, monedaPrincipal, pais });

  const { saldosPorFactura } = construirLedgerProveedor({
    provId: Number(provId),
    siiRecibidas: siiRes.data || [],
    facturasExternas: extRes.data || [],
    movimientosCaja: cajaRes.data || [],
    saldoInicial,
    monedaPrincipal,
    toDisplayCurrency: toDisp,
    vinculacionesDirectas: mbfRes.data || [],
  });

  return saldosPorFactura;
};
